import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Bell, Clock, AlertTriangle, Save, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ShiftReminderSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    shift_reminder_enabled: false,
    shift_reminder_time: '05:00',
    late_clockin_alert_enabled: false,
    late_clockin_minutes: 15
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/company/shift-reminder-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/company/shift-reminder-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Shift reminder settings saved');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/push/send-shift-reminders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        toast.error('Failed to send reminders');
      }
    } catch (error) {
      toast.error('Error sending reminders');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="shift-reminder-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Shift Reminder Settings
        </CardTitle>
        <CardDescription>
          Configure automatic push notifications for scheduled shifts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Shift Reminders */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-indigo-600" />
            <div>
              <p className="font-medium">Daily Shift Reminders</p>
              <p className="text-sm text-gray-500">Send push notifications to employees about their scheduled shifts</p>
            </div>
          </div>
          <Switch
            checked={settings.shift_reminder_enabled}
            onCheckedChange={(checked) => setSettings(s => ({ ...s, shift_reminder_enabled: checked }))}
            data-testid="shift-reminder-toggle"
          />
        </div>

        {settings.shift_reminder_enabled && (
          <div className="ml-8 p-4 border rounded-lg space-y-4">
            <div>
              <Label htmlFor="reminder-time">Reminder Time</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="reminder-time"
                  type="time"
                  value={settings.shift_reminder_time}
                  onChange={(e) => setSettings(s => ({ ...s, shift_reminder_time: e.target.value }))}
                  className="w-32"
                  data-testid="reminder-time-input"
                />
                <span className="text-sm text-gray-500">
                  Reminders will be sent daily at this time
                </span>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleSendNow} data-testid="send-now-btn">
              <Bell className="w-4 h-4 mr-2" />
              Send Reminders Now
            </Button>
          </div>
        )}

        {/* Late Clock-in Alerts */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <div>
              <p className="font-medium">Late Clock-in Alerts</p>
              <p className="text-sm text-gray-500">Alert managers when employees haven't clocked in on time</p>
            </div>
          </div>
          <Switch
            checked={settings.late_clockin_alert_enabled}
            onCheckedChange={(checked) => setSettings(s => ({ ...s, late_clockin_alert_enabled: checked }))}
            data-testid="late-alert-toggle"
          />
        </div>

        {settings.late_clockin_alert_enabled && (
          <div className="ml-8 p-4 border rounded-lg">
            <Label htmlFor="late-minutes">Alert After (minutes)</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                id="late-minutes"
                type="number"
                min="5"
                max="60"
                value={settings.late_clockin_minutes}
                onChange={(e) => setSettings(s => ({ ...s, late_clockin_minutes: parseInt(e.target.value) || 15 }))}
                className="w-20"
                data-testid="late-minutes-input"
              />
              <span className="text-sm text-gray-500">
                minutes after scheduled shift start
              </span>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} disabled={saving} data-testid="save-reminder-settings-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default ShiftReminderSettings;
